import { ReactElement, useState } from 'react';
import {
    UIFormInput,
    UIInlineAlert,
    UIModal,
    UIModalContent,
    UIModalFooter,
    UIModalHeader,
    UISwitch,
    UIText,
    UITextButton
} from '@/ui';
import { useBulkUpdateDeviceTiltAlertAction } from '../actions';
import { useBulkAlertLicenses } from '../queries';
import { useDevicesTranslation } from '../hooks';
import { PendingConfigNote } from './pending-config-note';

const DEFAULT_ANGLE = '45';
const DEFAULT_TIME_THRESHOLD = '180';

export type BulkTiltModalProps = {
    companyId: string;
    deviceIds: string[];
    isOpen: boolean;
    onClose: () => void;
    onApplied: () => void;
};

export function BulkTiltModal(props: BulkTiltModalProps): ReactElement {
    const { isOpen, onClose } = props;
    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            {isOpen ? <FormContent {...props} /> : null}
        </UIModal>
    );
}

function FormContent(props: BulkTiltModalProps): ReactElement {
    const { companyId, deviceIds, onClose, onApplied } = props;
    const { t } = useDevicesTranslation();
    const { bulkUpdateDeviceTiltAlert, loading } =
        useBulkUpdateDeviceTiltAlertAction({ companyId });
    const licenses = useBulkAlertLicenses({ companyId, deviceIds });

    const [enabled, setEnabled] = useState(true);
    const [angle, setAngle] = useState(DEFAULT_ANGLE);
    const [timeThreshold, setTimeThreshold] = useState(DEFAULT_TIME_THRESHOLD);
    const [showErrors, setShowErrors] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    function handleResetDefaults() {
        setAngle(DEFAULT_ANGLE);
        setTimeThreshold(DEFAULT_TIME_THRESHOLD);
        setShowErrors(false);
    }

    function angleError(): string {
        const value = Number(angle);
        if (!Number.isInteger(value) || value < 30 || value > 90) {
            return t('shared.fields.tilt_angle.error_invalid');
        }
        return '';
    }

    function timeError(): string {
        const value = Number(timeThreshold);
        if (!Number.isInteger(value) || value < 60 || value > 180) {
            return t('shared.fields.tilt_time.error_invalid');
        }
        return '';
    }

    const blocked = enabled && licenses.tilt.toEnable > licenses.tilt.free;

    function onApply() {
        setShowErrors(true);
        if (enabled && (angleError() || timeError())) {
            setShowAdvanced(true);
            return;
        }
        if (blocked) {
            return;
        }
        bulkUpdateDeviceTiltAlert(
            enabled
                ? {
                      deviceIds,
                      enabled: true,
                      angle: Number(angle),
                      timeThreshold: Number(timeThreshold)
                  }
                : { deviceIds, enabled: false }
        )
            .then(() => onApplied())
            .catch(() => {}); // Error handled in the action
    }

    return (
        <>
            <UIModalHeader
                title={t('components.bulk_tilt_modal.title')}
                subtitle={
                    deviceIds.length > 1
                        ? t('shared.devices.bulk_applies_to', {
                              count: deviceIds.length
                          })
                        : undefined
                }
            />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16
                    }}
                >
                    <PendingConfigNote />

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <UIText>{t('shared.devices.tilt_label')}</UIText>
                        </div>
                        <UISwitch checked={enabled} onChange={setEnabled} />
                    </div>

                    {enabled && (
                        <>
                            <div style={{ alignSelf: 'flex-start' }}>
                                <UITextButton
                                    type="button"
                                    variant="primary"
                                    leftIcon={
                                        showAdvanced
                                            ? 'chevron-up'
                                            : 'chevron-down'
                                    }
                                    onClick={() =>
                                        setShowAdvanced((show) => !show)
                                    }
                                >
                                    {t('shared.labels.advanced_settings')}
                                </UITextButton>
                            </div>
                            {showAdvanced && (
                                <>
                                    <UIFormInput
                                        label={t(
                                            'shared.devices.tilt_angle_label'
                                        )}
                                        description={t(
                                            'shared.devices.tilt_angle_description'
                                        )}
                                        type="number"
                                        value={angle}
                                        onChange={setAngle}
                                        errorText={
                                            showErrors ? angleError() : ''
                                        }
                                    />
                                    <UIFormInput
                                        label={t(
                                            'shared.devices.tilt_time_label'
                                        )}
                                        description={t(
                                            'shared.devices.tilt_time_description'
                                        )}
                                        type="number"
                                        value={timeThreshold}
                                        onChange={setTimeThreshold}
                                        errorText={
                                            showErrors ? timeError() : ''
                                        }
                                    />
                                </>
                            )}
                        </>
                    )}

                    {!licenses.loading && blocked && (
                        <UIInlineAlert
                            text={t(
                                'components.bulk_tilt_modal.not_enough_licences',
                                {
                                    count:
                                        licenses.tilt.toEnable -
                                        licenses.tilt.free
                                }
                            )}
                        />
                    )}
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'button',
                        text: t('shared.labels.apply'),
                        loading: loading,
                        disabled: loading || blocked || licenses.loading,
                        onClick: onApply
                    },
                    {
                        text: t('shared.labels.cancel'),
                        type: 'button',
                        disabled: loading,
                        onClick: onClose
                    },
                    ...(enabled && showAdvanced
                        ? [
                              {
                                  variant: 'textDefault' as const,
                                  type: 'button' as const,
                                  text: t('shared.labels.revert_default'),
                                  disabled: loading,
                                  onClick: handleResetDefaults
                              }
                          ]
                        : [])
                ]}
            />
        </>
    );
}
